import type {
  TransferPayload,
  TransferReasonKey
} from '../interface/transfer';

/**
 * Pure normalizer for the Transfer modal — the ONE source of truth for validation,
 * reused by the submit handler and by the per-field error flags the modal renders. Mirrors
 * `buildHoldPayload` (CDMX-18412) exactly in shape and intent.
 *
 * Two rules here exist because the design review rejected the first draft:
 *
 * 1. `selectedRecipientId` is a COMMITTED selection, never the input's text (review finding A3).
 *    A recipient typed but never chosen from the listbox is not a selection — AC4 requires that a
 *    valid person be *selected* before submit. The modal keeps input text and committed selection
 *    as separate state and only ever passes the latter here, so there is nothing to re-resolve
 *    against a result list that may have changed since (a valid server result need not appear in
 *    any locally-held array).
 *
 * 2. `other` NEVER fails open (review findings A7 / C10). The first draft required an "Other"
 *    dictionary id only when the dictionary happened to be non-empty — which, in the current
 *    no-dictionary-exists state, let `other` submit with no detail at all. Here, selecting `other`
 *    ALWAYS requires exactly one `otherReasonId`. The modal additionally renders the Other pill
 *    disabled when no dictionary is configured, so the combination is unreachable rather than
 *    merely rejected.
 */
export interface TransferFormValues {
  /** The committed recipient's id — `undefined` until one is chosen from the listbox. */
  readonly selectedRecipientId?: string;
  /** Multi-select ("select all that apply"); empty until the coder picks at least one. */
  readonly reasons: readonly TransferReasonKey[];
  /** Single-select dictionary value; only meaningful when `other` is among `reasons`. */
  readonly otherReasonId?: string;
  readonly note: string;
  readonly caseContext: string;
}

export type TransferValidationError =
  | 'recipient-required'
  | 'reason-required'
  | 'other-reason-required';

type Result =
  | { ok: true; payload: TransferPayload }
  | { ok: false; errors: TransferValidationError[] };

export function buildTransferPayload(values: TransferFormValues): Result {
  const errors: TransferValidationError[] = [];

  const recipientId = values.selectedRecipientId?.trim() ?? '';
  if (recipientId.length === 0) errors.push('recipient-required');

  const reasons = values.reasons;
  if (reasons.length === 0) errors.push('reason-required');

  // `other` is only ever valid WITH a configured dictionary value. See rule 2 above.
  const wantsOther = reasons.includes('other');
  const otherReasonId = values.otherReasonId?.trim() ?? '';
  if (wantsOther && otherReasonId.length === 0) {
    errors.push('other-reason-required');
  }

  const note = values.note.trim();
  const caseContext = values.caseContext.trim();

  if (errors.length > 0) return { ok: false, errors };

  const payload: TransferPayload = {
    recipientId,
    reasons: [...reasons],
    // Stale-field dropping: deselecting Other must not leave its id in the payload.
    ...(wantsOther ? { otherReasonId } : {}),
    ...(note.length > 0 ? { note } : {}),
    ...(caseContext.length > 0 ? { caseContext } : {})
  };
  return { ok: true, payload };
}
